import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/api';

const DISMISS_KEY = 'bb_verify_banner_dismissed';

const EmailVerificationBanner = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1'
  );
  const [cooldown, setCooldown] = useState(false);
  const [sending, setSending] = useState(false);

  if (!user || user.email_verified || dismissed) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const handleResend = async () => {
    if (cooldown || sending) return;
    setSending(true);
    try {
      await authApi.resendVerification();
      toast.success(t('verify_email.sent_toast'));
      setCooldown(true);
      setTimeout(() => setCooldown(false), 60000);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error(t('verify_email.cooldown'));
        setCooldown(true);
        setTimeout(() => setCooldown(false), 60000);
      } else {
        toast.error(err.response?.data?.detail || t('auth.something_went_wrong'));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2.5 flex items-center gap-3 text-sm">
      <Mail className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
      <div className="flex-1 text-amber-900 dark:text-amber-100 min-w-0">
        <span className="font-medium">{t('verify_email.banner_title')}</span>
        <span className="text-amber-800/90 dark:text-amber-200/80 ml-2">
          {t('verify_email.banner_desc', { email: user.email })}
        </span>
      </div>
      <button
        onClick={handleResend}
        disabled={cooldown || sending}
        className="px-3 py-1 rounded-md bg-amber-500/20 hover:bg-amber-500/30 text-amber-900 dark:text-amber-100 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
      >
        {sending ? '…' : t('verify_email.resend')}
      </button>
      <button
        onClick={handleDismiss}
        className="p-1 rounded hover:bg-amber-500/20 text-amber-700/70 hover:text-amber-900 dark:text-amber-200/60 dark:hover:text-amber-100 transition-colors shrink-0"
        aria-label={t('ui_common.dismiss')}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default EmailVerificationBanner;
